import { useEffect, useRef, useState } from "react";

export interface RuntimeMetrics {
  fps: number;
  frame: number;
  longTasks: number;
}

export function usePerformance(enabled = true): RuntimeMetrics {
  const [metrics, setMetrics] = useState<RuntimeMetrics>({
    fps: 60,
    frame: 16.7,
    longTasks: 0,
  });
  const frameTimes = useRef<number[]>([]);

  useEffect(() => {
    if (!enabled) return;
    let raf = 0;
    let last = performance.now();
    let lastPublish = last;
    let longTasks = 0;
    const observer =
      "PerformanceObserver" in window
        ? new PerformanceObserver((list) => {
            longTasks += list.getEntries().length;
          })
        : null;
    try {
      observer?.observe({ type: "longtask", buffered: true });
    } catch {
      // Long Task API is optional.
    }

    const tick = (now: number) => {
      const delta = now - last;
      last = now;
      if (delta < 250) frameTimes.current.push(delta);
      if (frameTimes.current.length > 120) frameTimes.current.shift();
      if (now - lastPublish > 700 && frameTimes.current.length) {
        const ordered = [...frameTimes.current].sort((a, b) => a - b);
        const p75 = ordered[Math.floor(ordered.length * 0.75)] ?? 16.7;
        const average =
          ordered.reduce((total, value) => total + value, 0) / ordered.length;
        setMetrics({
          fps: Math.min(60, Math.round(1000 / average)),
          frame: Math.round(p75 * 10) / 10,
          longTasks,
        });
        lastPublish = now;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
    };
  }, [enabled]);

  return metrics;
}
